
import React, { useMemo } from "react";
import { EventType } from "@/types/event";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { PlusCircle } from "lucide-react";

interface WeekViewProps {
  date: Date;
  events: EventType[];
  onSelectDate: (date: Date) => void;
  onSelectEvent: (event: EventType) => void;
  onCreateEvent?: (date: Date) => void;
  isDemoMode?: boolean;
}

const WeekView = ({ 
  date, 
  events, 
  onSelectDate, 
  onSelectEvent, 
  onCreateEvent,
  isDemoMode = false 
}: WeekViewProps) => {
  const { toast } = useToast();
  
  // Generate days of the week
  const weekDays = useMemo(() => {
    const days = [];
    const startOfWeek = new Date(date);
    
    // Adjust to start from Sunday (0) or the first day of the week
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      
      const isToday = dayDate.toDateString() === new Date().toDateString();
      
      // Get events for this day
      const dayEvents = events.filter(event => {
        const eventDate = new Date(event.startDate);
        return eventDate.toDateString() === dayDate.toDateString();
      });
      
      // Flag to indicate if this date has events
      const hasEvents = dayEvents.length > 0;
      
      days.push({
        date: dayDate,
        isToday,
        events: dayEvents,
        hasEvents
      });
    }
    
    return days;
  }, [date, events]);

  // Hours for the timeline (6AM to 10PM)
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);
  
  // Handle date selection
  const handleDateSelect = (day: any) => {
    onSelectDate(day.date);
  };
  
  // Handle hour cell click in a day column
  const handleHourClick = (day: any, hour: number) => {
    if (isDemoMode) {
      toast({
        title: "Demo Mode",
        description: "This is a demonstration calendar. Sign up for a full account to create events.",
        variant: "default",
      });
      return;
    }
    
    const selectedDate = new Date(day.date);
    selectedDate.setHours(hour);
    
    if (onCreateEvent) {
      onCreateEvent(selectedDate);
    } else {
      onSelectDate(selectedDate);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex min-w-max">
        {/* Time gutter */}
        <div className="w-16 flex-shrink-0">
          <div className="h-12"></div> {/* Empty space for day headers */}
          {hours.map(hour => (
            <div key={hour} className="h-16 border-t text-xs text-gray-500 pr-2 text-right">
              {hour % 12 === 0 ? '12' : hour % 12}{hour < 12 ? 'am' : 'pm'}
            </div>
          ))}
        </div>
        
        {/* Days columns */}
        <div className="flex-1 grid grid-cols-7 min-w-max">
          {/* Day headers */}
          {weekDays.map((day, index) => (
            <div 
              key={index}
              className={cn(
                "text-center p-2 border-b font-medium",
                day.isToday ? "bg-blue-50" : ""
              )}
              onClick={() => handleDateSelect(day)}
            >
              <div className="text-sm">{day.date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
              <div className={cn(
                "inline-flex items-center justify-center w-6 h-6 rounded-full text-sm",
                day.isToday ? "bg-blue-100 text-blue-700" : ""
              )}>
                {day.date.getDate()}
              </div>
              {day.hasEvents && (
                <span className="block mt-1 h-1 w-1 rounded-full bg-blue-500 mx-auto"></span>
              )}
            </div>
          ))}
          
          {/* Time slots */}
          {hours.map(hour => (
            <React.Fragment key={hour}>
              {weekDays.map((day, dayIndex) => (
                <div 
                  key={`${hour}-${dayIndex}`} 
                  className="h-16 border-t border-l relative hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleHourClick(day, hour)}
                >
                  {/* Render events at this hour */}
                  {day.events
                    .filter(event => new Date(event.startDate).getHours() === hour)
                    .map((event, i) => (
                      <div
                        key={i}
                        className={`absolute inset-x-1 rounded p-1 text-xs ${event.color} cursor-pointer`}
                        style={{
                          top: '4px',
                          height: 'calc(100% - 8px)',
                          zIndex: 10
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectEvent(event);
                        }}
                      >
                        <div className="font-medium truncate">{event.title}</div>
                        <div className="truncate">
                          {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
